import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../Redux/Store/Store';
import { getClient, getClientRolePermission, getAiPowered, getClientHasThirdPartySubsets } from '../../Redux/Store/Slices/client';
import { clearQuestionnaire } from '../../Redux/Store/Slices/questionnaire';
import { GetPagedEvaluateAreaDisplayInfosByClientAndUser } from '../../Redux/Store/Slices/area';
import { SetArchivedEvaluateAreaById } from '../../API/Evaluate/Evaluate';

import PagedCardCollection from '../../Components/Utils/LandingPages/Evaluate/PagedCardCollection';
import BulkExportWizard from '../../Components/Utils/LandingPages/BulkExportWizard/BulkExportWizard';
import CompactFilters, { ISelectOption } from '../../Components/Analyse/CompactFilters/CompactFilters';
import { dateRangeValue, namesToSortedOptions } from '../../Components/Analyse/CompactFilters/filterUtils';
import FilterSearchBar from '../../Components/SearchBar/FilterSearchBar/FilterSearchBar';
import Button from '../../Components/Utils/Button/Button';
import Tooltip from '../../Components/Utils/Tooltip/Tooltip';

import { AreaDisplayInfo } from '../../Types/AreaDisplayInfo/AreaDisplayInfo';
import { AnalyseDataRequest } from '../../Types/Paging/PagedRequest';
import { PagedResponse } from '../../Types/Paging/PagedResponse';
import { SetArchivedEvaluateAreaRequest } from '../../Types/Evaluate/SetArchivedEvaluateAreaRequest';

import styles from './LandingPages.module.css';

// Constants
const roleOptions: ISelectOption[] = [
    { label: 'Participant', value: 'Participant', required: false },
    { label: 'Reader', value: 'Reader', required: false },
    { label: 'Owner', value: 'Owner', required: false },
    { label: 'Lead', value: 'Lead', required: false },
];

type EvaluateActiveProps = {
    setShowImportTemplate: (show: boolean) => void;  
};
  
const EvaluateActive = ({ setShowImportTemplate }: EvaluateActiveProps) => {
    const dispatch = useAppDispatch();
    
    // Redux selectors
    const clientRolePermissions = useAppSelector(getClientRolePermission);
    const client = useAppSelector(getClient);
    const clientId = client?.id;
    const isAiPowered = useAppSelector(getAiPowered);
    const hasThirdPartySubsets = useAppSelector(getClientHasThirdPartySubsets);

    // Filter state
    const [selectedOrganisations, setSelectedOrganisations] = useState<ISelectOption[]>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<ISelectOption[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<ISelectOption[]>([]);
    const [selectedTemplateTypes, setSelectedTemplateTypes] = useState<ISelectOption[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<ISelectOption[]>([]);
    const [selectedOwners, setSelectedOwners] = useState<ISelectOption[]>([]);
    const [completedDate, setCompletedDate] = useState<ISelectOption[]>([]);

    // Data state
    const [displayInfos, setDisplayInfos] = useState<AreaDisplayInfo[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [bulkExportOpen, setBulkExportOpen] = useState(false);
    const [exportPreselectedId, setExportPreselectedId] = useState<string | undefined>();

    const openBulkExport = (areaId?: string) => {
        setExportPreselectedId(areaId);
        setBulkExportOpen(true);
    };

    const closeBulkExport = () => {
        setBulkExportOpen(false);
        setExportPreselectedId(undefined);
    };

    // Functions
    const fetchData = useCallback(async () => {
        setLoading(true);

        const request: AnalyseDataRequest = {
            partnerIds: [],
            departmentIds: [],
            groupIds: [],
            role: "",
            archived: false,
            softFilter: true,
            pageNumber: 1,
            pageSize: 100000
        };

        const result = await dispatch(GetPagedEvaluateAreaDisplayInfosByClientAndUser(request));
        const payload = result.payload as PagedResponse<AreaDisplayInfo>;
        const items = payload.items || [];
        
        setDisplayInfos(items);
        setLoading(false);
    }, [dispatch]);

    // Effects
    useEffect(() => {
        if (clientId && clientId !== 'undefined' && clientId !== 'null') {
            fetchData();
        }
    }, [clientId, fetchData]);

    useEffect(() => {
        dispatch(clearQuestionnaire());
    }, [dispatch]);

    const handleSearchTermChange = (value: string) => {
        setSearchTerm(value);
    };

    const toggleArchiveHandler = async (id: string) => {
        await SetArchivedEvaluateAreaById({ 
            areaId: id, 
            archived: true 
        } as SetArchivedEvaluateAreaRequest);
        fetchData();
    };

    const bulkSelectEnabled = displayInfos.some((a) => a.canExport);
  
    const filterConfig = useMemo(() => [
        { key: 'organisations', label: 'Organisations', placeholder: 'Select organisations', dataSource: 'api' as const },
        { key: 'departments', label: 'Departments', placeholder: 'Select departments', dataSource: 'api' as const },
        { key: 'groups', label: 'Groups', placeholder: 'Select groups', dataSource: 'api' as const },
        { key: 'owners', label: 'Owners', placeholder: 'Select owners', dataSource: 'props' as const, data: namesToSortedOptions(displayInfos.map(x => x.ownerName || '')) },
        { key: 'templateTypes', label: 'Types', placeholder: 'Select types', dataSource: 'props' as const, data: [] },
        { key: 'roles', label: 'Roles', placeholder: 'Select roles', dataSource: 'props' as const, data: roleOptions },
        { key: 'completedDate', label: 'Date Completed', placeholder: 'Pick a date range', kind: 'dateRange' as const },
    ], [displayInfos]);

    const selectedFilters = useMemo(() => ({
        organisations: hasThirdPartySubsets ? selectedOrganisations : [],
        departments: hasThirdPartySubsets ? selectedDepartments : [],
        groups: selectedGroups,
        owners: selectedOwners,
        templateTypes: selectedTemplateTypes,
        roles: selectedRoles,
        completedDate,
    }), [hasThirdPartySubsets, selectedOrganisations, selectedDepartments, selectedGroups, selectedOwners, selectedTemplateTypes, selectedRoles, completedDate]);

    const completedRange = useMemo(() => dateRangeValue(completedDate), [completedDate]);

    const handleFilterChange = (filters: Record<string, ISelectOption[]>) => {
        setSelectedOrganisations(filters.organisations || []);
        setSelectedDepartments(filters.departments || []);
        setSelectedGroups(filters.groups || []);
        setSelectedOwners(filters.owners || []);
        setSelectedTemplateTypes(filters.templateTypes || []);
        setSelectedRoles(filters.roles || []);
        setCompletedDate(filters.completedDate || []);
    };

    return (
        <div className={styles.listings}>
            <BulkExportWizard
                isVisible={bulkExportOpen}
                onClose={closeBulkExport}
                areas={displayInfos}
                productName="Evaluate"
                initialSelectedIds={exportPreselectedId ? [exportPreselectedId] : undefined}
            />
            <div className={styles.topContainer}>
                <FilterSearchBar
                    value={searchTerm}
                    onChange={handleSearchTermChange}
                    filters={filterConfig}
                    selectedFilters={selectedFilters}
                    onFilterChange={handleFilterChange}
                />
                <div className={styles.top}>
                    <CompactFilters
                        filters={filterConfig}
                        selectedFilters={selectedFilters}
                        onFilterChange={handleFilterChange}
                    />

                    <div className={styles.actionButtons}>
                        {bulkSelectEnabled && (
                            <Button
                                colour="rgb(36, 57, 79)"
                                textColour="#FFF"
                                clickHandler={() => openBulkExport()}
                            >
                                Export data
                            </Button>
                        )}
                        <Tooltip 
                        text={`Your role as a ${clientRolePermissions?.roleTitle} means you cannot import a template`} 
                        align="top" 
                        sideAlign="left" 
                        disabled={clientRolePermissions?.importTemplate}
                    >
                        <div className={styles.createButton}>
                                <Button 
                                    disabled={!clientRolePermissions?.importTemplate} 
                                    colour="rgb(36, 57, 79)" 
                                    textColour="#FFF" 
                                    clickHandler={() => setShowImportTemplate(true)}
                                >
                                    Create new diagnostic
                                </Button>
                        </div>
                    </Tooltip>
                    </div>
                </div>
            </div>

            <PagedCardCollection  
                template={false}
                displayInfos={displayInfos}
                archived={false}
                toggleArchiveHandler={toggleArchiveHandler}
                loading={loading}
                selectedRoleOption={selectedRoles}
                selectedPartners={selectedOrganisations}
                selectedDepartments={selectedDepartments}
                selectedGroups={selectedGroups}
                selectedOwners={selectedOwners}
                completedFrom={completedRange.from}
                completedTo={completedRange.to}
                searchTerm={searchTerm}
                selectable={false}
                hideExportButton
                onCardExport={bulkSelectEnabled ? openBulkExport : undefined}
            />
        </div>
    );  
};  
  
export default EvaluateActive;  
